import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Chip, LinearProgress,  Pagination, Paper, Snackbar, SnackbarCloseReason, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Typography, useTheme, alpha} from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FerramentasDaListagemCadastros } from '../../../shared/components/ferramentas-da-listagem/FerramentasDaListagemCadastros';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { getRowsPerPageCache, setRowsPerPageCache } from '../../../shared/utils/paginaRowsCache';
import { IListagemParceiros, ParceirosService } from '../../../shared/services/api/cadastros/ParceirosService';

export const ListagemDeParceiros: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestIdRef = useRef(0);
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [rows, setRows] = useState<IListagemParceiros[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openMsg, setOpenMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const ROWS_CACHE_KEY = 'parceiros';
  const [rowsPerPage, setRowsPerPage] = useState(() => getRowsPerPageCache(ROWS_CACHE_KEY) ?? Environment.LIMITE_DE_CADASTRO);
  const [containerHeight, setContainerHeight] = useState<number>();
  const tabelaRef = useRef<HTMLDivElement>(null);

  // Altura fixa de uma linha: maior conteúdo é o nome (Typography 0.9rem), mais o padding
  // vertical (py: 0.6 => 9.6px) e a borda. Todas as colunas usam noWrap, então a altura real
  // nunca varia com o conteúdo — por isso pode ser uma constante em vez de medida no DOM.
  // Medir uma linha real exigiria que os parceiros já tivessem chegado da API, obrigando uma
  // primeira requisição "de calibração" com um limite ainda errado e uma segunda corrigida.
  const ALTURA_LINHA_PADRAO = 32;

  const calcularLimite = () => {
    const container = tabelaRef.current;
    if (!container) return;

    // Mede o espaço real até o rodapé da janela em vez de usar um valor fixo de "calc(100vh - Npx)",
    // que ficava desalinhado com a altura real do título/toolbar e causava scroll na página inteira.
    const BOTTOM_GAP = 16;
    const alturaContainer = Math.max(200, window.innerHeight - container.getBoundingClientRect().top - BOTTOM_GAP);

    // O <thead> é sempre renderizado, mesmo sem parceiros carregados, então dá para medi-lo
    // logo no mount — diferente da linha do corpo, ele não depende de dados terem chegado.
    const headerEl = container.querySelector('thead');
    const alturaHeader = headerEl ? headerEl.getBoundingClientRect().height : 34;

    // Reservada como constante, nunca medida a partir do <tfoot>: a paginação só existe no
    // DOM quando totalCount > rowsPerPage, então medi-la criaria uma dependência circular
    // (ela aparece/desaparece conforme o próprio rowsPerPage muda).
    const FOOTER_RESERVADO = 56;
    const MIN_ROWS = 1;
    const MAX_ROWS = 100;
    const FOLGA_LINHAS = 1;
    const alturaDisponivel = alturaContainer - alturaHeader - FOOTER_RESERVADO;
    const novoLimite = Math.min(
      MAX_ROWS,
      Math.max(MIN_ROWS, Math.floor(alturaDisponivel / ALTURA_LINHA_PADRAO) - FOLGA_LINHAS)
    );

    setContainerHeight(prev => (prev === alturaContainer ? prev : alturaContainer));
    setRowsPerPageCache(ROWS_CACHE_KEY, novoLimite);
    setRowsPerPage(prev => (prev === novoLimite ? prev : novoLimite));
  };

  // useLayoutEffect (não useEffect) para rodar de forma síncrona antes da pintura e, mais
  // importante, antes do efeito de busca (que depende de rowsPerPage) disparar a requisição
  // inicial — assim a primeira busca já sai com o limite correto, sem uma segunda requisição
  // de correção.
  useLayoutEffect(() => {
    calcularLimite();
  }, []);

  useEffect(() => {
    window.addEventListener('resize', calcularLimite);
    return () => window.removeEventListener('resize', calcularLimite);
  }, []);

  const handleCloseMsg = (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setOpenMsg(false);
  };
  const tipo = useMemo(() => searchParams.get('tipo') || 'nome', [searchParams]);
  const filter = useMemo(() => searchParams.get('filter') || '', [searchParams]);
  const pagina = useMemo(() => Number(searchParams.get('pagina') || '1'), [searchParams]);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    debounce(() => {
      // Só zera a listagem quando a busca "debounced" realmente vai disparar (não a cada
      // tecla digitada), e sempre imediatamente antes do fetch — assim nunca fica visível
      // uma mistura de itens da pesquisa anterior com o resultado da pesquisa atual: ou
      // mostra o resultado antigo completo (enquanto o usuário ainda digita) ou mostra
      // "carregando" e depois só o resultado mais recente.
      if (requestId !== requestIdRef.current) return;
      setRows([]);
      setTotalCount(0);

      ParceirosService.getAll(pagina, filter, tipo, rowsPerPage)
        .then((result) => {
          if (requestId !== requestIdRef.current) return;
          setIsLoading(false);
          if (result instanceof Error) {
            setMsgType('error');
            setMsg(result.message);
            setOpenMsg(true);
          } else {
            setTotalCount(result.totalCount);
            setRows(result.data);
          }
        })
        .catch(() => {
          if (requestId !== requestIdRef.current) return;
          setIsLoading(false);
          setMsgType('error');
          setMsg('Erro ao carregar dados.');
          setOpenMsg(true);
        });
    });
  }, [pagina, filter, tipo, debounce, rowsPerPage]);

  useEffect(() => {
    if (pagina !== 1) {
      setSearchParams({ filter, tipo, pagina: '1' });
    }
  }, [rowsPerPage]);

  const formatarDocumento = (documento: string) => {
    if (!documento) return '';
    const apenasNumeros = documento.replace(/\D/g, '');

    if (apenasNumeros.length === 11) {
      return apenasNumeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (apenasNumeros.length === 14) {
      return apenasNumeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }

    return documento;
  };

  return (
    <LayoutBaseDePagina
      titulo='Listagem de Parceiros'
      barraDeFerramentas={
        <FerramentasDaListagemCadastros
          mostrarInputBusca
          mostrarInputBasicSelect
          textoDaBusca={filter}
          textoDoSelect={tipo}
          textoBotaoNovo='Novo Parceiro'
          aoClicarEmNovo={() => navigate('/parceiros/novo', { state: { from: `${location.pathname}${location.search}` } })}
          aoMudarTextoDeBusca={texto => setSearchParams({ filter: texto, tipo, pagina: '1' })}
          aoMudarTextoDoSelect={textoselect => setSearchParams({ filter, tipo: textoselect, pagina: '1' })}
          dadosSelecao={[
            { nameBD: 'nome', descricao: 'Nome / Razão Social' },
            { nameBD: 'cpf', descricao: 'CPF / CNPJ' },
            { nameBD: 'apelido', descricao: 'Apelido' },
          ]}
        />
      }
    >
      <Snackbar open={openMsg} autoHideDuration={6000} onClose={handleCloseMsg}>
        <Alert severity={msgType} variant='filled'>{msg}</Alert>
      </Snackbar>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer
          ref={tabelaRef}
          sx={{
            // 'auto' quando a página atual não preenche rowsPerPage (única página curta ou
            // última página parcial de uma listagem paginada), para o fundo acompanhar a
            // quantidade real de itens em vez de esticar até o fim da tela.
            height: rows.length >= rowsPerPage
              ? (containerHeight ? `${containerHeight}px` : 'calc(100vh - 160px)')
              : 'auto',
            maxHeight: containerHeight ? `${containerHeight}px` : 'calc(100vh - 160px)',
            overflow: 'auto',
            scrollbarGutter: 'stable',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,

                    backgroundColor: theme =>
                      theme.palette.mode === 'light'
                        ? alpha(theme.palette.primary.main, 0.08)
                        : alpha(theme.palette.primary.main, 0.16),

                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    border: 'none',
                    py: 1,
                  },
                }}
              >
                <TableCell>Nome</TableCell>
                <TableCell>CPF/CNPJ</TableCell>
                <TableCell>Fantasia</TableCell>
                <TableCell align='center'>Status</TableCell>
              </TableRow>
            </TableHead>

            {/* BODY */}
            <TableBody>
              {rows.map(row => (
                <TableRow
                  key={row.idparceiro}
                  hover
                  onClick={() => navigate(`/parceiros/${row.idparceiro}`, { state: { from: `${location.pathname}${location.search}` } })}
                  sx={{
                    cursor: 'pointer',

                    '& td': {
                      py: 0.6,
                      fontSize: '0.75rem'
                    },

                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.03)
                    }
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={700} fontSize="0.9rem" color="text.primary" noWrap>
                      {row.nome}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    <Typography variant="body2" fontWeight={700} fontFamily="monospace" fontSize="0.85rem" color="text.primary" noWrap>
                      {formatarDocumento(row.cpf)}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Typography variant="caption" noWrap sx={{ display: 'block' }}>
                      {row.fantasia || '—'}
                    </Typography>
                  </TableCell>

                  <TableCell align="center">
                    <Chip
                      label="Ativo"
                      size="small"
                      color="success"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem'
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={4}><LinearProgress /></TableCell>
                </TableRow>
              )}
              {totalCount > rowsPerPage && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Box display="flex" justifyContent="center" py={1}>
                      <Pagination
                        page={pagina}
                        count={Math.ceil(totalCount / rowsPerPage)}
                        onChange={(_, newPage) =>
                          setSearchParams({ filter, tipo, pagina: newPage.toString() })
                        }
                        size="small"
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>
    </LayoutBaseDePagina>
  );
};